"use client";

import React, { useState } from "react";
import Image from "next/image";
import Navigation from "@/components/Navigation";
import ModalSignUp from "@/components/Modals/ModalSignup"; // Fixed casing
import ModalSignIn from "@/components/Modals/ModalSignIn";
import { getUser, logout } from "@/app/actions";
import { User } from "@/types";
import { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import Link from "next/link";
import ModalPasswordReset from "./Modals/ModalPasswordReset";
import {
  Person24Filled,
  AddRegular,
  Person32Filled,
} from "@fluentui/react-icons";
import ModalEmailChange from "./Modals/ModalEmailChange";
import { useRouter } from "next/navigation";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import pb from "@/lib/pocketbase";
import { getFileUrl } from "@/utils/data";
import { User as UserIcon, SignOut, Bookmark, Heart, Briefcase } from "@phosphor-icons/react/dist/ssr";

export default function Header() {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | undefined>(undefined);

  const { data: userData } = useQuery<User | false>({
    queryKey: ["user-header"],
    queryFn: async () => {
      return ((await getUser()) as User) || false;
    },
  });

  const router = useRouter();
  const isUserLoggedIn = userData !== false;

  const handleLogout = async () => {
    await logout();
    pb.authStore.clear();
    queryClient.invalidateQueries({ queryKey: ["user"] });
    queryClient.clear();
  };

  return (
    <header className="fixed top-0 w-full z-30 bg-white shadow">
      <div className=" sm:mx-16 px-4 sm:px-6">
        <div className="flex items-center justify-between h-20 lg:h-28">
          <Navigation isUserLoggedIn={isUserLoggedIn} />

          {/* Desktop navigation */}
          <nav className="md:flex">
            {/* Desktop sign in links */}
            <div className="flex flex-row grow sm:grow-0 items-center pr-2 gap-x-2">
              <button
                onClick={() => {
                  if (!isUserLoggedIn) {
                    document.getElementById("sign-up-modal")?.click();
                  } else {
                    router.push("/classifieds/new");
                  }
                }}
                className={`btn btn-primary btn-sm lg:btn-md text-primary-content px-6 rounded-full font-regular`}
                id="sign-up-modal-button"
              >
                <AddRegular />
                <span className="hidden md:inline capitalize">Free Advert</span>
                {/* <span className="sm:hidden">New</span> */}
                <span className="md:hidden">Ad</span>
              </button>
              {!isUserLoggedIn ? (
                <div className="dropdown dropdown-end">
                  <div
                    tabIndex={0}
                    role="button"
                    className="btn btn-ghost btn-circle avatar"
                  >
                    <div className="w-6 rounded-full !flex justify-center items-center bg-base-300 lg:!hidden">
                      <Person24Filled />
                    </div>
                    <div className="w-10 rounded-full !hidden justify-center items-center bg-base-300 lg:!flex">
                      <Person32Filled />
                    </div>
                  </div>
                  <ul
                    tabIndex={0}
                    className="menu menu-sm dropdown-content mt-3 z-[1] p-2 shadow bg-base-100 rounded-box w-40"
                  >
                    <li className="flex justify-center">
                      <button
                        onClick={() =>
                          (async (router?: AppRouterInstance) => {
                            if (!isUserLoggedIn) {
                              document.getElementById("sign-in-modal")?.click();
                            } else {
                              router?.push("/account");
                            }
                          })()
                        }
                        className="btn btn-ghost text-normal capitalize flex items-center justify-start"
                      >
                        <div className="flex my-auto h-full items-center justify-start">
                          Log In
                        </div>
                      </button>
                    </li>
                    <li className="flex justify-center">
                      <button
                        onClick={() => {
                          localStorage.getItem("price") &&
                            localStorage.removeItem("price");
                          document.getElementById("sign-up-modal")?.click();
                        }}
                        className="btn btn-ghost text-normal capitalize flex items-center justify-start"
                        id="sign-up-modal-button"
                      >
                        <div className="flex my-auto h-full items-center justify-start">
                          Sign Up
                        </div>
                      </button>
                    </li>
                  </ul>
                </div>
              ) : (
                <div className="dropdown dropdown-end">
                  <div
                    tabIndex={0}
                    role="button"
                    className="btn btn-ghost btn-circle avatar"
                  >
                    {userData && userData.avatar ? (
                      <>
                        <div className="w-8 rounded-full !flex justify-center items-center lg:!hidden">
                          <Image
                            src={getFileUrl(
                              userData.collectionId,
                              userData.id,
                              userData.avatar
                            )}
                            alt="User Avatar"
                            className="w-6 h-6 rounded-full"
                          />
                        </div>
                        <div className="w-10 rounded-full !hidden justify-center items-center lg:!flex">
                        <Image
                            src={getFileUrl(
                              userData.collectionId,
                              userData.id,
                              userData.avatar
                            )}
                            alt="User Avatar"
                            className="w-10 h-10 rounded-full"
                          />
                        </div>
                      </>

                    ) : (
                      <>
                        <div className="w-6 rounded-full !flex justify-center items-center bg-base-300 lg:!hidden">
                          <Person24Filled />
                        </div>
                        <div className="w-10 rounded-full !hidden justify-center items-center bg-base-300 lg:!flex">
                          <Person32Filled />
                        </div>
                      </>
                    )}
                  </div>
                  <ul
                    tabIndex={0}
                    className="menu menu-sm dropdown-content mt-3 z-[1] p-2 shadow bg-base-100 rounded-box w-52"
                  >
                    <li>
                      <Link href="/account" aria-label="profile">
                        <div className="capitalize flex flex-row gap-x-2">
                          <UserIcon weight="fill" size={24} />
                          My Account
                        </div>
                      </Link>
                    </li>
                    <li>
                      <Link href="/account/saved-searches" aria-label="saved adverts">
                        <div className="capitalize flex flex-row gap-x-2">
                          <Bookmark weight="fill" size={24} />
                          Saved Adverts
                        </div>
                      </Link>
                    </li>
                    <li>
                      <Link href="/account/my-adverts" aria-label="my adverts">
                        <div className="capitalize flex flex-row gap-x-2">
                          <Briefcase weight="fill" size={24} />
                          My Adverts
                        </div>
                      </Link>
                    </li>
                    <li>
                      <Link href="/account/favourite-adverts" aria-label="favourite adverts">
                        <div className="capitalize flex flex-row gap-x-2">
                          <Heart weight="fill" size={24} />
                          Favourite Adverts
                        </div>
                      </Link>
                    </li>
                    <li onClick={handleLogout}>
                      <div className="capitalize flex flex-row gap-x-2">
                        <SignOut weight="bold" size={24} />
                        Logout
                      </div>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </nav>
        </div>
      </div>
      <ModalSignIn />
      <ModalPasswordReset />
      <ModalEmailChange />
      <ModalSignUp setUser={setUser} />
    </header>
  );
}
